import { SIM_REPORT_REASONS, type SimReportReason } from '@cruz/shared';
import { useId, useState } from 'react';
import { simErrorMessage } from './api.ts';
import { Sheet } from './Sheet.tsx';
import { inputClass, Notice, primaryButton, secondaryButton } from './ui.tsx';

/**
 * «Reportar»: a quiet link under an event or a shared card. It opens a sheet with the
 * reasons; the review panel gets it.
 */
export function ReportButton({
  what,
  onSend,
}: {
  /** "este evento", "esta tarjeta" */
  what: string;
  onSend: (reason: SimReportReason, comment: string) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  if (sent) {
    return (
      <p className="text-center text-sm text-ink-soft dark:text-cream/70">Reportado. Gracias.</p>
    );
  }
  return (
    <>
      <div className="text-center">
        <button
          type="button"
          className="rounded-full px-3 py-2 text-sm text-ink-soft underline-offset-4 hover:underline dark:text-cream/70"
          onClick={() => setOpen(true)}
        >
          Reportar {what}
        </button>
      </div>
      {open && (
        <ReportSheet
          what={what}
          onSend={onSend}
          onClose={() => setOpen(false)}
          onSent={() => {
            setOpen(false);
            setSent(true);
          }}
        />
      )}
    </>
  );
}

function ReportSheet({
  what,
  onSend,
  onClose,
  onSent,
}: {
  what: string;
  onSend: (reason: SimReportReason, comment: string) => Promise<unknown>;
  onClose: () => void;
  onSent: () => void;
}) {
  const id = useId();
  const [reason, setReason] = useState<SimReportReason | null>(null);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    if (!reason) return;
    setSending(true);
    setError(null);
    try {
      await onSend(reason, comment);
      onSent();
    } catch (err) {
      setError(simErrorMessage(err));
      setSending(false);
    }
  };

  return (
    <Sheet title={`Reportar ${what}`} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <fieldset className="space-y-2">
          <legend className="mb-2 text-base">¿Qué le pasa?</legend>
          {(Object.entries(SIM_REPORT_REASONS) as [SimReportReason, string][]).map(([k, label]) => (
            <label
              key={k}
              className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border px-4 py-2.5 ${
                reason === k
                  ? 'border-gold bg-cream-200 dark:bg-gold/15'
                  : 'border-purple/20 bg-paper dark:border-cream/20 dark:bg-night-700'
              }`}
            >
              <input
                type="radio"
                name={`${id}-reason`}
                value={k}
                checked={reason === k}
                onChange={() => setReason(k)}
                className="h-5 w-5 flex-none accent-purple dark:accent-gold"
              />
              {label}
            </label>
          ))}
        </fieldset>
        <div>
          <label htmlFor={`${id}-comment`} className="mb-1.5 block text-base">
            Cuéntanos algo más <span className="text-ink-soft dark:text-cream/60">(opcional)</span>
          </label>
          <textarea
            id={`${id}-comment`}
            value={comment}
            maxLength={500}
            rows={3}
            onChange={(e) => setComment(e.target.value)}
            className={`${inputClass} resize-y`}
          />
        </div>
        {error && <Notice tone="error">{error}</Notice>}
        <button type="submit" className={primaryButton} disabled={!reason || sending}>
          {sending ? 'Enviando…' : 'Enviar el reporte'}
        </button>
        <button type="button" className={secondaryButton} onClick={onClose}>
          Cancelar
        </button>
      </form>
    </Sheet>
  );
}
